import { inject, Injectable } from '@angular/core';
import { Cart, CartItem, UpdateCartItem } from '@softapps/wws/pos-core';
import { Scan } from '@softline/application';
import { ApiDataAccess } from '@softline/core';
import { Observable } from 'rxjs';
import {
  SOFTLINE_API_CASH_REGISTER_CART,
  SOFTLINE_API_CASH_REGISTER_CART_CONDITION_REASON_NECESSARY_CHECK,
  SOFTLINE_API_CASH_REGISTER_CART_CREDIT_LIMIT_CHECK,
  SOFTLINE_API_CASH_REGISTER_CART_DELETE,
  SOFTLINE_API_CASH_REGISTER_CART_DELETE_EMPTY_CARTS,
  SOFTLINE_API_CASH_REGISTER_CART_FINALIZE,
  SOFTLINE_API_CASH_REGISTER_CART_GET,
  SOFTLINE_API_CASH_REGISTER_CART_GET_TEMPLATE,
  SOFTLINE_API_CASH_REGISTER_CART_ITEM,
  SOFTLINE_API_CASH_REGISTER_CART_ITEM_DELETE,
  SOFTLINE_API_CASH_REGISTER_CART_ITEM_UPDATE,
  SOFTLINE_API_CASH_REGISTER_CART_RETURN,
  SOFTLINE_API_CASH_REGISTER_CART_TRANSFORM_RECEIPT,
  SOFTLINE_API_CASH_REGISTER_CART_UPDATE,
  SOFTLINE_API_CASH_REGISTER_CUSTOMER_CARD_SCAN,
  SOFTLINE_API_CASH_REGISTER_DOCUMENT_TYPE_GET,
  SOFTLINE_API_CASH_REGISTER_ITEMS_SCAN,
} from '@softapps/wws/pos-core';
import { CreditLimitResponse } from '@softapps/wws/pos-core';
import { Process } from '@softapps/wws/pos-core';

export interface ICartRepository {
  loadProcesses(): Promise<Process[]>;
  createProcess(): Promise<Process>;
  removeProcess(process: Process): Promise<void>;

  loadCart(cartId: number): Promise<Cart | null>;
  loadTemplateCart(): Promise<Cart>;
  addItem(
    id: number,
    productId: number,
    quantity: number,
    forceNewItem?: boolean
  ): Promise<Cart | null>;
  addItem$(
    id: number,
    productId: number,
    quantity: number,
    forceNewItem?: boolean
  ): Observable<Cart | null>;
  deleteItem(key: string, cartId: number): Promise<Cart>;
  updateItem(cartId: number, key: string, item: UpdateCartItem): Promise<Cart>;
  scanItem(cartId: number, scan: Scan, quantity: number): Promise<Cart>;
  loadDocumentTypes(id: number): Promise<string[]>;
  updateCart(id: number, changes: Partial<Cart>): Promise<Cart | null>;
  checkCreditLimit(id: number, body: object): Promise<CreditLimitResponse>;
  transformDocumentType(id: number): Promise<void>;
  checkConditionReasonNecessary(id: number): Promise<boolean>;
}

@Injectable()
export class CartRepository implements ICartRepository {
  readonly #dataAccess = inject(ApiDataAccess);

  async loadProcesses(): Promise<Process[]> {
    return this.#dataAccess.get({
      path: SOFTLINE_API_CASH_REGISTER_CART,
    });
  }

  async loadCart(cartId: number): Promise<Cart | null> {
    return this.#dataAccess.get({
      path: SOFTLINE_API_CASH_REGISTER_CART_GET,
      pathParams: {
        cartId: cartId,
      },
    });
  }

  async loadTemplateCart(): Promise<Cart> {
    try {
      return await this.#dataAccess.get({
        path: SOFTLINE_API_CASH_REGISTER_CART_GET_TEMPLATE,
      });
    } catch {
      return Promise.resolve({
        id: null,
        account: {
          id: 1,
          number: '1000',
          category: '',
          name: 'Barverkaufskunde',
        } as any,
        documentType: 'receipt',
        cashbookEntries: [],
        items: [],
        cashDiscount: 0,
        currency: 'EUR',
        downPayment: 0,
        net: 0,
        netIncl: 0,
        generalDiscount: 0,
        remainingAmountToPay: 0,
        address: undefined,
        accountId: null,
        collector: null,
        description: null,
        constructionside: null,
        constructionSiteId: null,
        customConstructionside: null,
        customerCard: null,
        deliveryAddress: null,
        documentFooter: null,
        documentHeader: null,
        referenceDocument: null,
        infos: [],
        distributionType: {
          id: 1,
          name: 'Selbstabholung',
          value: 'Selbstabholung',
          label: 'Selbstabholung',
          icon: 'fa-regular fa-hand text-3xl text-primary',
        },
      });
    }
  }

  async createProcess(): Promise<Process> {
    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_CASH_REGISTER_CART,
      },
      {}
    );
  }

  async removeProcess(process: Process): Promise<void> {
    await this.#dataAccess.delete({
      path: SOFTLINE_API_CASH_REGISTER_CART_DELETE,
      pathParams: { cartId: process.id },
    });
  }

  async removeEmptyCarts(): Promise<unknown> {
    return this.#dataAccess.delete({
      path: SOFTLINE_API_CASH_REGISTER_CART_DELETE_EMPTY_CARTS,
    });
  }

  async updateCart(
    id: number,
    changes: Partial<Cart> & {
      customerCardNumber?: string;
      sellOffType?: string;
      note?: object;
    }
  ): Promise<Cart | null> {
    return await this.#dataAccess.patch(
      {
        path: SOFTLINE_API_CASH_REGISTER_CART_UPDATE,
        pathParams: {
          cartId: id,
        },
      },
      changes
    );
  }

  async loadDocumentTypes(id?: number | undefined | null): Promise<string[]> {
    return (
      await this.#dataAccess.get<{ documentTypes: string[] }>({
        path: SOFTLINE_API_CASH_REGISTER_DOCUMENT_TYPE_GET,
        queryParams: id ? { cartId: id } : undefined,
      })
    )?.documentTypes;
  }

  async addItem(
    id: number,
    productId: number,
    quantity = 1,
    forceNewItem?: boolean
  ): Promise<Cart | null> {
    const queryParams = forceNewItem ? { forceNewItem: true } : undefined;

    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_CASH_REGISTER_CART_ITEM,
        pathParams: { cartId: id },
        queryParams,
      },
      {
        productId: productId,
        quantity: quantity,
      }
    );
  }

  addItem$(
    id: number,
    productId: number,
    quantity = 1,
    forceNewItem?: boolean
  ): Observable<Cart | null> {
    const queryParams = forceNewItem ? { forceNewItem: true } : undefined;
    return this.#dataAccess.create$(
      {
        path: SOFTLINE_API_CASH_REGISTER_CART_ITEM,
        pathParams: { cartId: id },
        queryParams,
      },
      {
        productId: productId,
        quantity: quantity,
      }
    );
  }

  async deleteItem(key: string, cartId: number): Promise<Cart> {
    return await this.#dataAccess.delete({
      path: SOFTLINE_API_CASH_REGISTER_CART_ITEM_DELETE,
      pathParams: { key, cartId: cartId },
    });
  }

  async updateItem(cartId: number, key: string, update: UpdateCartItem): Promise<Cart> {
    return await this.#dataAccess.patch(
      {
        path: SOFTLINE_API_CASH_REGISTER_CART_ITEM_UPDATE,
        pathParams: {
          cartId: cartId,
          key,
        },
      },
      update
    );
  }

  updateItem$(cartId: number, key: string, item: Partial<CartItem>): Observable<Cart> {
    return this.#dataAccess.patch$(
      {
        path: SOFTLINE_API_CASH_REGISTER_CART_ITEM_UPDATE,
        pathParams: {
          cartId: cartId,
          key,
        },
      },
      item
    );
  }

  async scanItem(
    cartId: number,
    scan: Scan,
    quantity: number,
    forceNewItem = false
  ): Promise<Cart> {
    const queryParams = forceNewItem ? { forceNewItem: true } : undefined;

    return await this.#dataAccess.create(
      {
        path: SOFTLINE_API_CASH_REGISTER_ITEMS_SCAN,
        pathParams: {
          cartId: cartId,
          type: scan.labelType,
          code: scan.data,
        },
        queryParams,
      },
      {
        quantity: quantity,
      }
    );
  }

  scanItem$(
    cartId: number,
    scan: Scan,
    quantity: number,
    forceNewItem = false
  ): Observable<Cart> {
    const queryParams = forceNewItem ? { forceNewItem: true } : undefined;

    return this.#dataAccess.create$(
      {
        path: SOFTLINE_API_CASH_REGISTER_ITEMS_SCAN,
        pathParams: {
          cartId: cartId,
          type: scan.labelType,
          code: scan.data,
        },
        queryParams,
      },
      {
        quantity: quantity,
      }
    );
  }

  scanCustomerCard$(
    scan: Scan
  ): Observable<{ customerNumber?: number; customerCardNumber?: string } | null> {
    return this.#dataAccess.get$({
      path: SOFTLINE_API_CASH_REGISTER_CUSTOMER_CARD_SCAN,
      pathParams: {
        type: scan.labelType,
        code: scan.data,
      },
    });
  }

  async checkCreditLimit(cartId: number, body: object): Promise<CreditLimitResponse> {
    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_CASH_REGISTER_CART_CREDIT_LIMIT_CHECK,
        pathParams: {
          cartId: cartId,
        },
      },
      body
    );
  }

  async finalize(cartId: number, body: any): Promise<any> {
    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_CASH_REGISTER_CART_FINALIZE,
        pathParams: {
          cartId: cartId,
        },
      },
      body
    );
  }

  async createReturnCart(body: object): Promise<Cart> {
    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_CASH_REGISTER_CART_RETURN,
      },
      body
    );
  }

  async transformDocumentType(id: number, account?: any, copies?: number): Promise<void> {
    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_CASH_REGISTER_CART_TRANSFORM_RECEIPT,
        pathParams: {
          id,
        },
      },
      copies !== undefined ? { ...(account ?? {}), copies } : (account ?? {})
    );
  }

  async checkConditionReasonNecessary(id: number): Promise<boolean> {
    return (
      await this.#dataAccess.create<object, { required: boolean }>(
        {
          path: SOFTLINE_API_CASH_REGISTER_CART_CONDITION_REASON_NECESSARY_CHECK,
          pathParams: { id },
        },
        {}
      )
    )?.required;
  }
}
